import { ChangeDetectionStrategy, Component, effect, input, output, signal } from '@angular/core';

/** A feedback reporter's identity, collected once and persisted by the host. */
export interface ReporterIdentity {
  name: string;
  email: string;
}

/**
 * Modal that collects the reporter's name + email before they leave feedback.
 * Owns its own form state and validation; emits the validated identity on
 * Continue. The host decides when to show it ([open]) and what to do with the
 * result.
 */
@Component({
  selector: 'app-identity-modal',
  templateUrl: './identity-modal.html',
  styleUrls: ['./identity-modal.scss', './identity-modal.premium.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class IdentityModal {
  /** Whether the modal is shown. */
  readonly open = input(false);

  /** Emits the validated identity when the reporter continues. */
  readonly save = output<ReporterIdentity>();

  /** Emits when the reporter dismisses the modal (Cancel / scrim / Esc). */
  readonly cancel = output<void>();

  protected readonly name = signal('');
  protected readonly email = signal('');
  protected readonly nameInvalid = signal(false);
  protected readonly emailInvalid = signal(false);

  constructor() {
    // Start from a clean form each time the modal opens.
    effect(() => {
      if (this.open()) {
        this.name.set('');
        this.email.set('');
        this.nameInvalid.set(false);
        this.emailInvalid.set(false);
      }
    });
  }

  /** Validates the form and, if valid, emits the identity. */
  protected onContinue(): void {
    const name = this.name().trim();
    const email = this.email().trim();
    // A single @ with a dotted domain and no whitespace.
    const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
    this.nameInvalid.set(!name);
    this.emailInvalid.set(!emailValid);
    if (!name || !emailValid) {
      return;
    }
    this.save.emit({ name, email });
  }
}
